import { useSelector } from 'react-redux';
import { RootState } from '~/store';
import { LoanType } from '@qred/shared-enums';
import useTranslate from './useTranslate';
import { logToSentry } from '~/helpers/loggers.helper';
import { notifications } from '@qred/components-library';
import { MARKETS_WITH_EXTERNAL_LOAN_TERMS_LINKS } from '~/constants/markets';

const useExternalLoanLinks = (loanType: LoanType) => {
  const t = useTranslate();
  const {
    intl: { market },
  } = useSelector((state: RootState) => state);

  const shouldLinkExternalSite = MARKETS_WITH_EXTERNAL_LOAN_TERMS_LINKS.includes(
    market
  );
  const isAnnuityLoan = loanType === LoanType.ANNUITY_LOAN;

  const openExternalLink = (urlKey: string, sentryErrorName: string) => {
    const url = t(urlKey) as string;
    if (!url || url === urlKey) {
      logToSentry(
        new Error(`Missing URL for ${urlKey} in market ${market}`),
        sentryErrorName
      );
      notifications.show({
        state: 'critical',
        message: t('SomethingWentWrong'),
        opened: true,
      });
      return;
    }
    window.open(url, '_blank', 'noopener noreferrer');
  };

  const openGeneralTerms = () => {
    openExternalLink('LinkUrls.LoanGeneralTerms', 'MissingLoanGeneralTermsUrl');
  };

  const openProductTerms = () => {
    const urlKey = isAnnuityLoan
      ? 'LinkUrls.InvestmentLoanTerms'
      : 'LinkUrls.FlexLoanTerms';
    openExternalLink(urlKey, 'MissingProductTermsUrl');
  };

  const openPriceList = () => {
    openExternalLink('LinkUrls.PriceList', 'MissingPriceListUrl');
  };

  const productTermsLinkText = isAnnuityLoan
    ? (t('OnboardingOffer.ProductTermsInvestmentLoanLinkText') as string)
    : (t('OnboardingOffer.ProductTermsFlexLoanLinkText') as string);

  return {
    shouldLinkExternalSite,
    openGeneralTerms,
    openProductTerms,
    openPriceList,
    productTermsLinkText,
  };
};

export default useExternalLoanLinks;
